import { useState, useEffect, type ChangeEvent } from 'react';
import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import TextField from '../../components/TextField';
import FormSection from '../../components/FormSection';

// --- Types ---

interface Campaign {
  id: string;
  name: string;
}

type LeadType = 'Caregiver' | 'Patient' | '';

interface FaithFormState {
  firstName: string;
  lastName: string;
  mobile: string;
  email: string;
  caregiverType: string;
}

interface NautilusFormState {
  firstName: string;
  lastName: string;
  mobile: string;
  email: string;
}

interface Questionnaire {
  longTermCareInsurance: string;
  medicare: string;
  medicationReminders: string;
  errands: string;
  lightHousekeeping: string;
  recentFalls: string;
}

type FieldErrors = Record<string, string>;

const CAREGIVER_TYPES = ['HHA', 'CNA', 'RN', 'LPN', 'PT', 'PTA', 'OT', 'OTA', 'SLP'];

const EMPTY_FAITH_FORM: FaithFormState = { firstName: '', lastName: '', mobile: '', email: '', caregiverType: '' };
const EMPTY_NAUTILUS_FORM: NautilusFormState = { firstName: '', lastName: '', mobile: '', email: '' };
const EMPTY_QUESTIONNAIRE: Questionnaire = { longTermCareInsurance: '', medicare: '', medicationReminders: '', errands: '', lightHousekeeping: '', recentFalls: '' };

const QUESTIONNAIRE_FIELDS: { key: keyof Questionnaire; labelKey: string }[] = [
  { key: 'longTermCareInsurance', labelKey: 'Long Term Care Insurance' },
  { key: 'medicare', labelKey: 'Medicare' },
  { key: 'medicationReminders', labelKey: 'Medication Reminders' },
  { key: 'errands', labelKey: 'Errands' },
  { key: 'lightHousekeeping', labelKey: 'Light Housekeeping' },
  { key: 'recentFalls', labelKey: 'Recent Falls (past 3 months)' },
];

const paperSx = {
  my: 2,
  p: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: { xs: '95%', sm: '90%', md: '75%', lg: '60%', xl: '50%' },
  maxWidth: '800px',
  minWidth: '320px',
  mx: 'auto',
};

const disabledButtonSx = {
  '&.Mui-disabled': { backgroundColor: '#ccc', color: 'white' },
};

// TabPanel matching old project
function TabPanel({ children, value, index, ...other }: { children: React.ReactNode; value: number; index: number; [key: string]: unknown }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return { id: `simple-tab-${index}`, 'aria-controls': `simple-tabpanel-${index}` };
}

// --- Component ---

export default function MarketerEntry() {
  const { t } = useTranslation();
  const { authFetch } = useAuth();

  // Setup state
  const [leadType, setLeadType] = useState<LeadType>('');
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [hasNautilus, setHasNautilus] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [showTabs, setShowTabs] = useState(false);

  // Tab & form state
  const [tabValue, setTabValue] = useState(0);
  const [hasProceededToForm, setHasProceededToForm] = useState(false);
  const [activeCompanyForm, setActiveCompanyForm] = useState<'faith' | 'nautilus' | null>(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showNautilusLeadForm, setShowNautilusLeadForm] = useState(false);

  // Faith form
  const [faithForm, setFaithForm] = useState<FaithFormState>(EMPTY_FAITH_FORM);
  const [faithErrors, setFaithErrors] = useState<FieldErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  // Nautilus form
  const [nautilusForm, setNautilusForm] = useState<NautilusFormState>(EMPTY_NAUTILUS_FORM);
  const [questionnaire, setQuestionnaire] = useState<Questionnaire>(EMPTY_QUESTIONNAIRE);
  const [nautilusErrors, setNautilusErrors] = useState<FieldErrors>({});
  const [isNautilusSaving, setIsNautilusSaving] = useState(false);

  // Alert
  const [showAlert, setShowAlert] = useState<{ open: boolean; type: 'success' | 'error'; message: string }>({ open: false, type: 'success', message: '' });

  // Restore from localStorage
  useEffect(() => {
    const storedLeadType = localStorage.getItem('marketerLeadType') as LeadType;
    const storedCompanyForm = localStorage.getItem('marketerActiveForm') as 'faith' | 'nautilus' | null;
    if (storedLeadType) {
      setLeadType(storedLeadType);
      if (storedCompanyForm === 'nautilus') {
        setShowNautilusLeadForm(true);
        setHasProceededToForm(true);
        setActiveCompanyForm('nautilus');
      } else if (storedCompanyForm === 'faith') {
        setShowLeadForm(true);
        setHasProceededToForm(true);
        setActiveCompanyForm('faith');
      }
    }
  }, []);

  // Fetch companies
  useEffect(() => {
    let cancelled = false;
    authFetch('/api/marketer/companies')
      .then(async (res) => {
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          setHasNautilus(data.hasNautilus || false);
          const shouldShowTabs = data.hasNautilus || false;
          setShowTabs(shouldShowTabs);
          if (data.hasNautilus) setTabValue(1);
        }
      })
      .catch(() => { setShowTabs(false); setTabValue(0); })
      .finally(() => { if (!cancelled) setIsLoadingCompanies(false); });
    return () => { cancelled = true; };
  }, [authFetch]);

  // Fetch campaigns
  useEffect(() => {
    let cancelled = false;
    setIsLoadingCampaigns(true);
    authFetch('/api/marketer/campaigns')
      .then(async (res) => {
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          setCampaigns(data.campaigns || []);
          const storedId = localStorage.getItem('marketerCampaignId');
          if (storedId) {
            const found = (data.campaigns || []).find((c: Campaign) => c.id === storedId);
            if (found) setCampaign(found);
          }
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setIsLoadingCampaigns(false); });
    return () => { cancelled = true; };
  }, [authFetch]);

  // Background effect
  useEffect(() => {
    document.body.style.backgroundImage = "url('/images/backgrounds/smiling-nurse.png')";
    document.body.style.backgroundRepeat = 'no-repeat';
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'bottom';
    document.body.style.backgroundAttachment = 'fixed';
    return () => {
      document.body.style.backgroundImage = '';
      document.body.style.backgroundRepeat = '';
      document.body.style.backgroundSize = '';
      document.body.style.backgroundPosition = '';
      document.body.style.backgroundAttachment = '';
    };
  }, []);

  // --- Handlers ---

  const handleSaveSelections = () => {
    if (!leadType) {
      setShowAlert({ open: true, type: 'error', message: 'Please select a lead type' });
      return;
    }
    localStorage.setItem('marketerLeadType', leadType);
    localStorage.setItem('marketerActiveForm', 'faith');
    if (campaign) localStorage.setItem('marketerCampaignId', campaign.id);
    else localStorage.removeItem('marketerCampaignId');
    setShowLeadForm(true);
    setHasProceededToForm(true);
    setActiveCompanyForm('faith');
  };

  const handleSaveNautilusSelections = () => {
    localStorage.setItem('marketerLeadType', 'Patient');
    localStorage.setItem('marketerActiveForm', 'nautilus');
    if (campaign) localStorage.setItem('marketerCampaignId', campaign.id);
    else localStorage.removeItem('marketerCampaignId');
    setShowNautilusLeadForm(true);
    setHasProceededToForm(true);
    setActiveCompanyForm('nautilus');
  };

  // --- Faith form ---

  const handleFaithChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { name?: string; value: unknown } }) => {
    const name = (e.target as any).name as string;
    const value = (e.target as any).value as string;
    setFaithForm((prev) => ({ ...prev, [name]: value }));
    if (faithErrors[name]) setFaithErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateFaith = (): boolean => {
    const errs: FieldErrors = {};
    if (!faithForm.firstName.trim()) errs.firstName = 'First Name is required';
    if (!faithForm.lastName.trim()) errs.lastName = 'Last Name is required';
    const digits = faithForm.mobile.replace(/\D/g, '');
    if (digits.length !== 10) errs.mobile = 'Mobile must be exactly 10 digits';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(faithForm.email.trim())) errs.email = 'Invalid email format';
    if (leadType === 'Caregiver' && !faithForm.caregiverType) errs.caregiverType = 'Caregiver Type is required';
    setFaithErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleFaithSubmit = async () => {
    if (!validateFaith()) return;
    setIsSaving(true);
    try {
      const res = await authFetch('/api/marketer/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: faithForm.firstName,
          lastName: faithForm.lastName,
          mobile: faithForm.mobile,
          email: faithForm.email,
          leadType,
          caregiverType: faithForm.caregiverType || undefined,
          campaignId: campaign?.id || undefined,
        }),
      });
      if (res.ok) {
        setShowAlert({ open: true, type: 'success', message: 'Thank you for submitting!' });
        setFaithForm(EMPTY_FAITH_FORM);
        setFaithErrors({});
      } else {
        const data = await res.json().catch(() => ({}));
        setShowAlert({ open: true, type: 'error', message: data.message || 'Error creating entry' });
      }
    } catch {
      setShowAlert({ open: true, type: 'error', message: 'Error creating entry.' });
    } finally {
      setIsSaving(false);
    }
  };

  // --- Nautilus form ---

  const handleNautilusChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNautilusForm((prev) => ({ ...prev, [name]: value }));
    if (nautilusErrors[name]) setNautilusErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleQuestionnaireChange = (field: keyof Questionnaire, value: string) => {
    setQuestionnaire((prev) => ({ ...prev, [field]: value }));
    if (nautilusErrors[field]) setNautilusErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateNautilus = (): boolean => {
    const errs: FieldErrors = {};
    if (!nautilusForm.firstName.trim()) errs.firstName = 'First Name is required';
    if (!nautilusForm.lastName.trim()) errs.lastName = 'Last Name is required';
    const digits = nautilusForm.mobile.replace(/\D/g, '');
    if (digits.length !== 10) errs.mobile = 'Mobile must be exactly 10 digits';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(nautilusForm.email.trim())) errs.email = 'Invalid email format';
    for (const { key } of QUESTIONNAIRE_FIELDS) {
      if (!questionnaire[key]) errs[key] = 'Required';
    }
    setNautilusErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNautilusSubmit = async () => {
    if (!validateNautilus()) return;
    setIsNautilusSaving(true);
    try {
      const res = await authFetch('/api/marketer/lead/nautilus', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: nautilusForm.firstName,
          lastName: nautilusForm.lastName,
          mobile: nautilusForm.mobile,
          email: nautilusForm.email,
          campaignId: campaign?.id || undefined,
          questionnaire,
        }),
      });
      if (res.ok) {
        setShowAlert({ open: true, type: 'success', message: 'Thank you for submitting!' });
        setNautilusForm(EMPTY_NAUTILUS_FORM);
        setQuestionnaire(EMPTY_QUESTIONNAIRE);
        setNautilusErrors({});
      } else {
        const data = await res.json().catch(() => ({}));
        setShowAlert({ open: true, type: 'error', message: data.message || 'Error creating entry' });
      }
    } catch {
      setShowAlert({ open: true, type: 'error', message: 'Error creating entry.' });
    } finally {
      setIsNautilusSaving(false);
    }
  };

  // --- Render: Faith lead form card ---

  const renderFaithForm = () => (
    <Box sx={{ width: '100%', pt: 2 }}>
      <FormSection
        title={leadType === 'Caregiver' ? 'Caregiver Interest Form' : 'Patient Interest Form'}
        icon={<PersonIcon />}
      >
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <TextField label="First Name" name="firstName" value={faithForm.firstName} onChange={handleFaithChange} fullWidth required error={!!faithErrors.firstName} helperText={faithErrors.firstName || ''} />
          </Grid2>
          <Grid2 size={12}>
            <TextField label="Last Name" name="lastName" value={faithForm.lastName} onChange={handleFaithChange} fullWidth required error={!!faithErrors.lastName} helperText={faithErrors.lastName || ''} />
          </Grid2>
          <Grid2 size={12}>
            <TextField inputMask="999 999 9999" maskChar={null} label="Mobile Phone" name="mobile" value={faithForm.mobile || ''} onChange={handleFaithChange} error={!!faithErrors.mobile} helperText={faithErrors.mobile || ''} fullWidth required />
          </Grid2>
          <Grid2 size={12}>
            <TextField label="Email" name="email" value={faithForm.email} onChange={handleFaithChange} fullWidth required error={!!faithErrors.email} helperText={faithErrors.email || ''} />
          </Grid2>
          {leadType === 'Caregiver' && (
            <Grid2 size={12}>
              <FormControl fullWidth error={!!faithErrors.caregiverType} required>
                <InputLabel id="caregiver-type-label">Caregiver Type</InputLabel>
                <Select
                  labelId="caregiver-type-label"
                  name="caregiverType"
                  value={faithForm.caregiverType}
                  onChange={(e) => handleFaithChange(e as any)}
                  label="Caregiver Type"
                >
                  {CAREGIVER_TYPES.map((ct) => (
                    <MenuItem key={ct} value={ct}>{ct}</MenuItem>
                  ))}
                </Select>
                {faithErrors.caregiverType && <FormHelperText>{faithErrors.caregiverType}</FormHelperText>}
              </FormControl>
            </Grid2>
          )}
        </Grid2>
      </FormSection>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button variant="contained" onClick={handleFaithSubmit} disabled={isSaving}>
          {isSaving ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </Box>
    </Box>
  );

  // --- Render: Nautilus lead form card ---

  const renderNautilusForm = () => (
    <Box sx={{ width: '100%', pt: 2 }}>
      <FormSection title="Homecare Interest Form" icon={<PersonIcon />}>
        <Grid2 container spacing={2} sx={{ mb: 3 }}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField label="First Name" name="firstName" value={nautilusForm.firstName} onChange={handleNautilusChange} fullWidth required error={!!nautilusErrors.firstName} helperText={nautilusErrors.firstName || ''} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField label="Last Name" name="lastName" value={nautilusForm.lastName} onChange={handleNautilusChange} fullWidth required error={!!nautilusErrors.lastName} helperText={nautilusErrors.lastName || ''} />
          </Grid2>
          <Grid2 size={12}>
            <TextField inputMask="999 999 9999" maskChar={null} label="Phone Number" name="mobile" value={nautilusForm.mobile || ''} onChange={handleNautilusChange} error={!!nautilusErrors.mobile} helperText={nautilusErrors.mobile || ''} fullWidth required />
          </Grid2>
          <Grid2 size={12}>
            <TextField label="Email" name="email" value={nautilusForm.email} onChange={handleNautilusChange} fullWidth required error={!!nautilusErrors.email} helperText={nautilusErrors.email || ''} />
          </Grid2>
        </Grid2>

        <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold', color: '#702b9d' }}>
          Please answer the following questions:
        </Typography>

        <Grid2 container spacing={3}>
          {QUESTIONNAIRE_FIELDS.map(({ key, labelKey }) => (
            <Grid2 size={12} key={key}>
              <FormControl error={!!nautilusErrors[key]} required>
                <FormLabel sx={{ color: '#330662', fontWeight: 'bold', mb: 1 }}>{labelKey}</FormLabel>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {['Yes', 'No', 'N/A'].map((option) => (
                    <Button
                      key={option}
                      variant={questionnaire[key] === option ? 'contained' : 'outlined'}
                      onClick={() => handleQuestionnaireChange(key, option)}
                      sx={{
                        backgroundColor: questionnaire[key] === option ? '#330662' : 'transparent',
                        borderColor: '#330662',
                        color: questionnaire[key] === option ? 'white' : '#330662',
                        '&:hover': {
                          backgroundColor: questionnaire[key] === option ? '#7300c2' : 'rgba(137, 0, 225, 0.1)',
                          borderColor: '#330662',
                        },
                        minWidth: '80px',
                        textTransform: 'none',
                      }}
                    >
                      {option}
                    </Button>
                  ))}
                </Box>
                {nautilusErrors[key] && <FormHelperText sx={{ color: 'error.main' }}>{nautilusErrors[key]}</FormHelperText>}
              </FormControl>
            </Grid2>
          ))}
        </Grid2>
      </FormSection>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button variant="contained" onClick={handleNautilusSubmit} disabled={isNautilusSaving}>
          {isNautilusSaving ? <CircularProgress size={24} /> : 'Submit'}
        </Button>
      </Box>
    </Box>
  );

  // --- Render: Setup content (lead type + campaign) ---

  const renderSetupContent = () => (
    <>
      <Typography variant="h5" gutterBottom>Lead Entry Setup</Typography>
      <Box sx={{ width: '100%', maxWidth: 600, mt: 3 }}>
        <Card variant="outlined" sx={{ mb: 3, p: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
              <Button
                variant={leadType === 'Caregiver' ? 'contained' : 'outlined'}
                onClick={() => setLeadType('Caregiver')}
                size="large"
                sx={{ minWidth: 120 }}
              >
                Caregiver
              </Button>
              <Button
                variant={leadType === 'Patient' ? 'contained' : 'outlined'}
                onClick={() => setLeadType('Patient')}
                size="large"
                sx={{ minWidth: 120 }}
              >
                Patient
              </Button>
            </Box>
            <Autocomplete
              options={campaigns}
              getOptionLabel={(option) => option.name || ''}
              value={campaign}
              onChange={(_e, newVal) => setCampaign(newVal)}
              loading={isLoadingCampaigns}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Campaign"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingCampaigns ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </CardContent>
        </Card>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button variant="contained" onClick={handleSaveSelections} disabled={!leadType} sx={disabledButtonSx}>
            Continue to Lead Form
          </Button>
        </Box>
      </Box>
    </>
  );

  // --- Render: Nautilus setup content (campaign only, no lead type) ---

  const renderNautilusSetupContent = () => (
    <>
      <Typography variant="h5" gutterBottom>Lead Entry Setup</Typography>
      <Box sx={{ width: '100%', maxWidth: 600, mt: 3 }}>
        <Card variant="outlined" sx={{ mb: 3, p: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Autocomplete
              options={campaigns}
              getOptionLabel={(option) => option.name || ''}
              value={campaign}
              onChange={(_e, newVal) => setCampaign(newVal)}
              loading={isLoadingCampaigns}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Campaign"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingCampaigns ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </CardContent>
        </Card>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button variant="contained" onClick={handleSaveNautilusSelections}>
            Continue to Lead Form
          </Button>
        </Box>
      </Box>
    </>
  );

  // --- Main render ---

  return (
    <Container component="main" maxWidth="lg">
      <Box
        sx={{
          flex: '0 1 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: '100%',
          transform: { xs: 'scale(0.95)', sm: 'scale(0.98)', md: 'scale(1)' },
          transformOrigin: 'center top',
          transition: 'transform 0.3s ease',
        }}
      >
        {isLoadingCompanies ? (
          <Paper elevation={3} sx={{ ...paperSx, p: 3 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>Loading...</Typography>
          </Paper>
        ) : hasProceededToForm ? (
          /* Form view (locked in) */
          <Paper elevation={3} sx={paperSx}>
            {activeCompanyForm === 'faith' && showLeadForm && renderFaithForm()}
            {activeCompanyForm === 'nautilus' && showNautilusLeadForm && renderNautilusForm()}
          </Paper>
        ) : showTabs ? (
          /* Tab view (Faith + Nautilus) */
          <Paper elevation={3} sx={paperSx}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={tabValue}
                  onChange={(_e, newVal) => setTabValue(newVal)}
                  aria-label="company tabs"
                  sx={{
                    '& .MuiTabs-indicator': { backgroundColor: '#330662' },
                    '& .Mui-selected': { color: '#330662 !important' },
                    '& .MuiTab-root:hover': { color: '#330662' },
                  }}
                >
                  <Tab label="Faith/Nurses Guild/P&M" {...a11yProps(0)} />
                  <Tab label="Nautilus" {...a11yProps(1)} />
                </Tabs>
              </Box>

              <TabPanel value={tabValue} index={0}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  {renderSetupContent()}
                </Box>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                  {renderNautilusSetupContent()}
                </Box>
              </TabPanel>
            </Box>
          </Paper>
        ) : (
          /* No tabs (Faith only) */
          <>
            {!showLeadForm && (
              <Paper elevation={3} sx={{ ...paperSx, p: 3 }}>
                {renderSetupContent()}
              </Paper>
            )}
            {showLeadForm && (
              <Paper elevation={3} sx={paperSx}>
                {renderFaithForm()}
              </Paper>
            )}
          </>
        )}
      </Box>

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isSaving || isNautilusSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={showAlert.open}
        autoHideDuration={4000}
        onClose={() => setShowAlert((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowAlert((prev) => ({ ...prev, open: false }))} severity={showAlert.type} variant="filled">
          {showAlert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
